import React, { FC, InputHTMLAttributes, useId } from 'react';
import ASSETS from '../../assets';
import { twMerge } from 'tailwind-merge';

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  showButton?: boolean;
  onSubmit?: () => void;
  error?: string;
  isNotCrossIcon?: boolean;
  buttonDisabled?: boolean;
  onClear?: () => void;
  helperText?: string;
  required?: boolean;
  showPasswordToggle?: boolean;
  showPassword?: boolean;
  onTogglePassword?: () => void;
}

const TextField: FC<IProps> = ({
  value,
  label,
  showButton,
  onSubmit,
  className,
  readOnly,
  error,
  isNotCrossIcon,
  buttonDisabled,
  onClear,
  helperText,
  required,
  showPasswordToggle,
  showPassword,
  onTogglePassword,
  type,
  ...rest
}) => {
  const id = useId();

  return (
    <div className="w-full">
      <label htmlFor={id} className="block">
        <div
          className={twMerge(
            'w-full max-w-[488px] h-[60px] border border-gary-400 flex flex-col p-3 rounded-[12px] relative group focus-within:border-k-dark-blue focus-within:shadow-lg transition-all duration-300 ease-in-out',
            className,
            readOnly &&
              'border-none focus-within:border-none focus-within:shadow-none bg-k-grey-200',
            error &&
              'border-k-error focus-within:shadow-red-200 focus-within:border-k-error'
          )}
        >
          <input
            className='w-full peer outline-none border-none text-lg mt-auto placeholder:text-transparent bg-transparent text-gray-800'
            placeholder=' '
            value={value}
            type={showPasswordToggle ? (showPassword ? 'text' : 'password') : type}
            {...rest}
            id={id}
            readOnly={readOnly}
          />
          <p
            className={twMerge(
              `text-[16px] text-k-grey-text absolute top-[16px] left-3 peer-[:focus]:text-xs peer-[:focus]:top-[4px] peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:top-[4px] transition-all duration-300 ease-in-out pointer-events-none`,
              error && 'text-k-error',
              required && 'after:content-["*"] after:text-red-500 after:ml-1'
            )}
          >
            {label}
          </p>
          
          {/* Action buttons */}
          <div className="absolute top-1/2 right-3 transform -translate-y-1/2 flex items-center gap-2">
            {showPasswordToggle && (
              <div
                className='size-8 rounded-full grid place-items-center cursor-pointer hover:bg-gray-100 transition-all duration-200'
                role='button'
                tabIndex={0}
                onClick={() => {
                  if (typeof onTogglePassword === 'function') onTogglePassword();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (typeof onTogglePassword === 'function') onTogglePassword();
                  }
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />

                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </div>
            )}
            {showButton && value && !error && (
              <div
                className='size-8 bg-k-dark-blue rounded-full grid place-items-center cursor-pointer hover:bg-opacity-90 transition-all duration-200'
                role='button'
                onClick={() => {
                  if (typeof onSubmit === 'function' && !buttonDisabled) onSubmit();
                }}
              >
                <img src={ASSETS.arrow_right_white} alt='Submit' className="w-4 h-4" />
              </div>
            )}
            {error && !isNotCrossIcon && (
              <div
                onClick={() => {
                  if (onClear) onClear();
                }}
                className='size-8 rounded-full grid place-items-center cursor-pointer hover:bg-gray-100 transition-all duration-200'
              >
                <img src={ASSETS.cross_red} alt='Clear' className="w-4 h-4" />
              </div>
            )}
          </div>
        </div>
      </label>
      
      {/* Helper text and error message */}
      {(error || helperText) && (
        <div className="mt-2 px-1">
          {error && (
            <p className="text-sm text-k-error flex items-center gap-1">
              <span className="text-red-500">⚠</span>
              {error}
            </p>
          )}
          {helperText && !error && (
            <p className="text-sm text-k-grey-text">
              {helperText}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TextField;
